// src/pages/Clients.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { db } from "../firebase/config";
import { collection, addDoc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import ClientCard from "../components/ClientCard";
import { useNavigate } from "react-router-dom";

const Container = styled.div`
  padding: 2rem;
  min-height: 100vh;
  border-radius: 25px;
  color: var(--fontPrimaryColor);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
`;

const AddClientButton = styled.button`
  background: #111;
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: #333;
  }
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

/* MODAL */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 50;
`;

const ModalBox = styled.div`
  background: #1b2130;
  padding: 2rem;
  border-radius: 18px;
  width: 400px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #444;
  background: #2c3447;
  color: white;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.6rem 1.1rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 500;
`;

const PrimaryButton = styled(Button)`
  background: #10b981;
  color: white;

  &:hover {
    background: #0a9468;
  }
`;

const CancelButton = styled(Button)`
  background: #444;
  color: white;

  &:hover {
    background: #555;
  }
`;

export default function Clients() {
  const { currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [clientName, setClientName] = useState("");
  const [goal, setGoal] = useState("");
  const navigate = useNavigate();

  const openClientDetails = (client) => {
    navigate(`/clients/${client.id}`);
  };

  useEffect(() => {
    if (!currentUser) return;
    const col = collection(db, "users", currentUser.uid, "clients");
    const unsubscribe = onSnapshot(col, (snapshot) => {
      setClients(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, [currentUser]);

  const addClient = async () => {
    if (clientName.trim() === "") return;

    if (!currentUser) return;
    await addDoc(collection(db, "users", currentUser.uid, "clients"), {
      name: clientName,
      goal: goal || "No goal yet",
      progress: 0,
      createdAt: new Date(),
    });

    setClientName("");
    setGoal("");
    setShowModal(false);
  };

  return (
    <Container>
      <Header>
        <Title>Clients</Title>
        <AddClientButton onClick={() => setShowModal(true)}>
          + Add Client
        </AddClientButton>
      </Header>

      <ClientsGrid>
        {clients.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            onClick={openClientDetails}
          />
        ))}
      </ClientsGrid>

      {showModal && (
        <ModalOverlay>
          <ModalBox>
            <h2>Add New Client</h2>
            <Input
              placeholder="Client name..."
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
            <Input
              placeholder="Goal… (optional)"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            />

            <ButtonRow>
              <CancelButton onClick={() => setShowModal(false)}>
                Cancel
              </CancelButton>
              <PrimaryButton onClick={addClient}>Save Client</PrimaryButton>
            </ButtonRow>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
}
